import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import type { OtpDeliveryStatus } from '@/api/types';

/**
 * Where a WhatsApp sign-in code has got to. Meta accepts a send before it checks the
 * number, so "this number isn't on WhatsApp" only arrives a few seconds later — this
 * polls the API every 2 seconds for up to 30 and then stops.
 */
export type OtpDeliveryState = 'idle' | 'sending' | 'delivered' | 'not_on_whatsapp' | 'failed' | 'timeout';

export type DeliveryLookup = (deliveryId: string) => Promise<OtpDeliveryStatus>;

const POLL_MS = 2_000;
const GIVE_UP_MS = 30_000;

const stateOf = ({ status, reason }: OtpDeliveryStatus): OtpDeliveryState => {
  if (status === 'delivered' || status === 'read') return 'delivered';
  if (status === 'failed') return reason === 'not_on_whatsapp' ? 'not_on_whatsapp' : 'failed';
  return 'sending';
};

export const useOtpDelivery = (
  deliveryId: string | undefined,
  lookup: DeliveryLookup = api.auth.otpDelivery,
): OtpDeliveryState => {
  const [result, setResult] = useState<{ id: string; state: OtpDeliveryState } | null>(null);

  useEffect(() => {
    if (!deliveryId) return undefined;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const started = Date.now();

    const poll = async () => {
      let state: OtpDeliveryState = 'sending';
      try {
        state = stateOf(await lookup(deliveryId));
      } catch {
        // Offline for a moment: keep trying until the deadline.
      }
      if (stopped) return;
      if (state === 'sending' && Date.now() - started >= GIVE_UP_MS) state = 'timeout';
      setResult({ id: deliveryId, state });
      if (state === 'sending') timer = setTimeout(() => void poll(), POLL_MS);
    };

    timer = setTimeout(() => void poll(), POLL_MS);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [deliveryId, lookup]);

  if (!deliveryId) return 'idle';
  return result?.id === deliveryId ? result.state : 'sending';
};
